import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/types/client";

export const useUpdateClient = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...clientData }: Partial<Client> & { id: string }) => {
      console.log("Updating client with data:", { id, ...clientData });
      
      // فحص التكرار عند التحديث
      if (clientData.name_ar || clientData.name_en || clientData.mobile) {
        const orFilterParts: string[] = [];
        
        if (clientData.name_ar) {
          orFilterParts.push(`name_ar.eq.${clientData.name_ar}`);
        }
        if (clientData.name_en) {
          orFilterParts.push(`name_en.eq.${clientData.name_en}`);
        }
        if (clientData.mobile) {
          orFilterParts.push(`mobile.eq.${clientData.mobile}`);
        }

        if (orFilterParts.length > 0) {
          const { data: existingClient, error: checkError } = await supabase
            .from('clients')
            .select('id')
            .or(orFilterParts.join(','))
            .neq('id', id) // استبعاد العميل الحالي
            .limit(1)
            .maybeSingle();

          if (checkError) {
            console.error("Error checking for existing client:", checkError);
            throw checkError;
          }

          if (existingClient) {
            throw new Error("يوجد عميل آخر بنفس البيانات");
          }
        }
      }
      
      // Prepare the data for update
      const updateData = {
        name_ar: clientData.name_ar,
        name_en: clientData.name_en,
        mobile: clientData.mobile,
        additional_info: clientData.additional_info,
        passport_info: clientData.passport_info,
        attachments: clientData.attachments,
        status: clientData.status || 'waiting',
        company_id: clientData.company_id === '' ? null : clientData.company_id,
        // Keep company field for backward compatibility if no company_id
        company: !clientData.company_id ? clientData.company : null,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from("clients")
        .update(updateData)
        .eq("id", id)
        .select(`
          *,
          companies:company_id (
            id,
            name_ar
          ),
          creator:created_by (
            id,
            name
          ),
          client_assignments (
            id,
            role,
            employee_id,
            employees (
              id,
              name
            )
          )
        `)
        .single();
      
      if (error) {
        console.error("Error updating client:", error);
        throw error;
      }
      
      const transformedData = {
        ...data,
        company: data.companies?.name_ar || data.company,
        employees: data.creator, // For backward compatibility
      }
      
      console.log("Client updated successfully:", transformedData);
      return transformedData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", data.id] });
    },
  });
};
